import { MenuItem } from "@mui/material";
import { capitalize } from "lodash";

import { Box } from "@/Components/v2/atoms/Box";
import { Divider } from "@/Components/v2/atoms/Divider";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { Select } from "@/Components/v2/atoms/Select";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan, Typography } from "@/Components/v2/atoms/Typography";

import { useChat } from "../../context/ChatContext";

const ModelDescriptions: Record<string, string> = {
  CLAUDE:
    "More inquisitive. Better for deep diving into complex queries and per product/channel analysis.",
  GEMINI: "More direct. Better for high-level queries about overall performance.",
};

export const ModelSelect = ({ width: _width = "280px" }: { width?: string }) => {
  const { metadata, selectedModel, setSelectedModel } = useChat();

  if (!metadata?.availableModels?.length) {
    return null;
  }

  const options = metadata.availableModels.map((model: string) => ({
    value: model,
    label: capitalize(model),
    description: (ModelDescriptions as any)[model] || "AI model for answering questions",
    disabled: model === "GEMINI",
  }));

  return (
    <FormControl size="small" sx={{ width: "fit-content" }}>
      <Select
        labelId="model-select-label"
        id="model-select"
        value={selectedModel || ""}
        onChange={(event) => {
          setSelectedModel(event.target.value as "CLAUDE" | "GEMINI");
        }}
        renderValue={(value) => (
          <Typography fontSize={14}>
            <BoldSpan>Base Model: </BoldSpan>
            {options?.find((o) => o.value === value)?.label}
          </Typography>
        )}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          PaperProps: {
            sx: { maxWidth: "320px" },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        }}
      >
        {options?.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            sx={{ py: 1 }}
          >
            <Stack direction="column" gap={0.5}>
              <Typography fontWeight={600} fontSize={14}>
                {option.label}
              </Typography>
              <Typography fontSize={12} whiteSpace="normal">
                {option.description}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ px: 2, py: 0.5 }}>
          <Typography fontSize={12}>
            <BoldSpan>Note: </BoldSpan>
            <Typography fontSize={12} component="span" fontStyle="italic">
              All models are hosted on Mutinex Cloud Storage. Your data remains fully segregated and
              will never be shared.
            </Typography>
          </Typography>
        </Box>
      </Select>
    </FormControl>
  );
};
